import { useWidget } from '@telescope/cassini-hooks';
import { Box, Button, FormControl } from '@telescope/cassini-ui';
import { Heading, Text } from 'components';
import { GenericObject } from '@telescope/cassini-utilities';
import React, { useState } from 'react';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router-dom';
import reactStringReplace from 'react-string-replace';
import { ErrorType } from 'types';
import { ROUTES } from 'utils';
import { useAuth } from '../lib';
import { AuthMethod, AuthType } from '../utils';
import { RegisterFormStep1 } from './RegisterFormStep1';
import { RegisterFormStep2 } from './RegisterFormStep2';
import { ErrorWarning } from './ErrorWarning';
import { Open, Closed } from 'generated-types/widget';

export const Register = () => {
  const { registerWithEmail, registerWithFacebook } = useAuth();
  const { data } = useWidget({ select: (data: Open) =>  data.widget.global_views.auth });
  const { content: { registrationModal: content } } = data!;

  const [currentStep, setCurrentStep] = useState(1);
  const [formError, setFormError] = useState<React.ReactNode>(null);
  const [defaultValues, setDefaultValues] = useState();
  const navigate = useNavigate();
  const { data: errorCopy } = useWidget({ select: (data: Open | Closed) => data.widget.global_views.errors });
  const { content: errorContent } = errorCopy!;

  const invalidEmailError = reactStringReplace(content.errorMessage.message, '{{LINK}}', (match, i) => (
    <Button
      fontSize="inherit"
      variant="link"
      onClick={() => navigate(ROUTES.LOGIN, { replace: true, state: { backgroundLocation: ROUTES.HOME } })}
      key={i}
      minW={0}>
      {content.errorMessage.linkText}
    </Button>
  ));

  const handleRegisterStep1 = async (formData: any) => {
    // TODO: This logic is really weird and not intuitive. Figure out a way to improve this. It's weird that we have to do a login call on the register method, and that the login call is expected to throw an error.

    try {
      let res;
      if (formData.method === AuthMethod.EMAIL) {
        res = await registerWithEmail({ payload: formData, checkIfUserAlreadyRegistered: true });
      }
      if (formData.method === AuthMethod.FACEBOOK) {
        res = await registerWithFacebook({ payload: formData, checkIfUserAlreadyRegistered: true });
      }

      // User is registered already, Prompt to login
      if (res?.isUserAlreadyRegistered) {
        setFormError(invalidEmailError);
        return;
      }

      // User is not registered yet, go to next step
      setCurrentStep(2);
      setFormError(null);
      setDefaultValues({ ...formData, ...res?.user });
    } catch (err: any) {
      if (err.message === ErrorType.FB_PERMISSIONS) {
        return setFormError(errorContent.fbPermissions.copy.description);
      }

      setFormError(errorContent.generic.copy.description);
    }
  };

  const handleRegisterStep2 = async (formData: GenericObject) => {
    try {
      if (formData.method === AuthMethod.EMAIL) {
        await registerWithEmail({ payload: formData, checkIfUserAlreadyRegistered: false });
      }

      if (formData.method === AuthMethod.FACEBOOK) {
        await registerWithFacebook({ payload: formData, checkIfUserAlreadyRegistered: false });
      }
      TagManager.dataLayer({ dataLayer: { event: 'sign_up', method: formData.method } });

      navigate(ROUTES.HOME, { replace: true });
    } catch (err: any) {
      if (err.message === AuthType.SWEEPS) {
        navigate(ROUTES.LOGIN, { replace: true });
        return;
      }

      setFormError(errorContent.generic.copy.description);
    }
  };

  return (
    <Box textAlign="center">
      <Heading as="h2" mb={10} allowMarkdown>
        {content.headline}
      </Heading>

      <Text allowMarkdown fontSize={{base: 'sm', md: 'md'}} px={[5,5,0]} mb={8} textAlign={'center'}> 
        {data?.content.description} 
      </Text>

      <FormControl isInvalid={!!formError}>
        <ErrorWarning>
          {formError}
        </ErrorWarning>
      </FormControl>

      {currentStep === 1 && <RegisterFormStep1 onFormSubmit={handleRegisterStep1} />}
      {currentStep === 2 && <RegisterFormStep2 onFormSubmit={handleRegisterStep2} defaultValues={defaultValues} />}
      
    </Box>
  );
};
